import webservice from '../../../../services/webservice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { AesEncrypt, AesDecrypt } from '../../../../services/crypto';

// Fetch users list (paginated)
export const fetchUsersList = createAsyncThunk('Users/fetchUsersList', async (params) => {
    try {
        const payload = AesEncrypt({
            ...params,
            limit: params.limit || 10,
            search: params.search || '',
            sortBy: params.sortBy || 'createdDate',
            sortOrder: params.sortOrder || 'desc'
        });

     //   console.log("params : ", params);
        const response = await axios.post(`${webservice}api/getUsersList`, { EncData: payload }, { signal: params.signal });
        if (response.data.response === 'S') {
            const decrypted = AesDecrypt(response.data.data);
            return { success: true, data: decrypted.data || [], totalCount: decrypted.totalCount || 0 };
        }
        return { success: false, data: [], totalCount: 0 };
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        throw error;
    }
});

// Fetch users summary (KPI cards)
export const fetchUsersSummary = createAsyncThunk('Users/fetchUsersSummary', async (params) => {
    try {
        const payload = AesEncrypt({ ...params });
        const response = await axios.post(`${webservice}api/getUsersSummary`, { EncData: payload }, { signal: params.signal });
        if (response.data.response === 'S') {
            const decrypted = AesDecrypt(response.data.data);
            return { success: true, ...decrypted };
        }
        return { success: false };
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        throw error;
    }
});

// Fetch users analytics (Charts)
export const fetchUsersAnalytics = createAsyncThunk('Users/fetchUsersAnalytics', async (params) => {
    try {
        const payload = AesEncrypt({ ...params });
        const response = await axios.post(`${webservice}api/getUsersAnalytics`, { EncData: payload }, { signal: params.signal });
        if (response.data.response === 'S') {
            const decrypted = AesDecrypt(response.data.data);
            return { success: true, ...decrypted };
        }
        return { success: false };
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        throw error;
    }
});

// Legacy wrapper (Parallelized)
export const fetchAllUsers = createAsyncThunk('Users/fetchAllUsers', async (params) => {
    try {
        const payload = AesEncrypt({
            ...params,
            limit: params.limit || 10,
            search: params.search || '',
            sortBy: params.sortBy || 'createdDate',
            sortOrder: params.sortOrder || 'desc'
        });

        // Parallel execution for maximum performance
        const [listRes, summaryRes, analyticsRes] = await Promise.all([
            axios.post(`${webservice}api/getUsersList`, { EncData: payload }, { signal: params.signal }),
            axios.post(`${webservice}api/getUsersSummary`, { EncData: payload }, { signal: params.signal }),
            axios.post(`${webservice}api/getUsersAnalytics`, { EncData: payload }, { signal: params.signal })
        ]);

        let combinedData = {
            success: true,
            data: [],
            totalCount: 0,
            analytics: {}
        };

        if (listRes.data.response === 'S') {
            const decrypted = AesDecrypt(listRes.data.data);
            combinedData.data = decrypted.data || [];
            combinedData.totalCount = decrypted.totalCount || 0;
        }

        const summaryData = summaryRes.data.response === 'S' ? AesDecrypt(summaryRes.data.data) : {};
        const analyticsData = analyticsRes.data.response === 'S' ? AesDecrypt(analyticsRes.data.data) : {};

        combinedData.analytics = {
            ...summaryData,
            ...analyticsData
        };

        return combinedData;
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        console.error('Error in parallel fetchAllUsers:', error);
        throw error;
    }
});

// Fetch screen-wise audit log data with demographic filters
export const fetchAllScreens = createAsyncThunk('Users/fetchAllScreens', async (params) => {
    try {
        const payload = AesEncrypt({
            page: params.page || 1,
            limit: params.limit || 10,
            userId: params.userId || '',
            age: params.age || '',
            gender: params.gender || '',
            maritalStatus: params.maritalStatus || '',
            profession: params.profession || '',
            location: params.location || '',
            bmi: params.bmi || '',
            searchTerm: params.searchTerm || '',
            sortField: params.sortField || 'count',
            sortOrder: params.sortOrder || 'desc'
        });

        const response = await axios.post(`${webservice}api/getAllScreenData`, { EncData: payload }, { signal: params.signal });

        if (response.data.response === 'S') {
            const decrypted = AesDecrypt(response.data.data);
            return {
                success: true,
                data: decrypted?.data || [],
                totalCount: Number(decrypted?.totalCount) || 0,
                page: decrypted?.page || 1,
                limit: decrypted?.limit || 10
            };
        }
        return { success: false, data: [], totalCount: 0 };
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        throw error;
    }
});

// Fetch demographic insights for a specific screen
export const fetchScreenInsights = createAsyncThunk('Users/fetchScreenInsights', async (params) => {
    try {
        const payload = AesEncrypt({ screenName: params.screenName });
        const response = await axios.post(`${webservice}api/getScreenInsights`, { EncData: payload }, { signal: params.signal });
        if (response.data.response === 'S') {
            const decrypted = AesDecrypt(response.data.data);
            return { success: true, screenName: decrypted.screenName, insights: decrypted.insights };
        }
        return { success: false, message: response.data.message };
    } catch (error) {
        if (axios.isCancel(error)) return { cancelled: true };
        throw error;
    }
});
